import {
  decimalToPip,
  encodeFixedIncomeVaultConfigurationFields,
  encodeProfitShareVaultConfigurationFields,
  pipToDecimal,
} from '@katanaperps/katana-perps-sdk';
import { ContractTransactionResponse } from 'ethers';

import { ApiVault } from '#/components/vaults/shared/types';
import { trimTo8Decimals } from '#/lib/numbers';
import { UnreachableCaseError } from '#/lib/utils';
import { IWallet } from '#/reduxState/wallet/walletReducer';

import {
  FixedIncomeVaultProvider_v1,
  ProfitShareVaultProvider_v1,
} from '../contractsFromSdk';

import {
  getVaultProviderContract,
  getKatanaWalletSigner,
} from './vaultsContract';

/**
 * @params Interest Multiplier in in PIPs
 * @returns APY must by multiplied by 100 to get % value
 */
export const convertVaultInterestMultiplierToAPY = (
  interestMultiplier: bigint
) => {
  return Math.exp(Number(pipToDecimal(interestMultiplier))) - 1;
};

export function convertVaultApyToInterestMultiplierInPips(
  apyPercent: number | string
): bigint {
  return decimalToPip(trimTo8Decimals(Math.log(1 + Number(apyPercent) / 100)));
}

/** Percent form value ("10" = 10%) to a pips multiplier (0.10000000) */
const convertPercentToMultiplierInPips = (percent: number | string) =>
  decimalToPip(trimTo8Decimals(Number(percent) / 100));

/** Configuration fields shared by both vault providers (decimals from PIPs) */
type VaultTransformedConfigBase = {
  managerWallet: string;
  maximumNetDeposits: string;
  /** 3600s = 1 hour */
  minimumUnappliedDepositOrWithdrawalAgeInSToInitiateExit: bigint;
  /** Withdrawal rate, 0.2 = 20% */
  withdrawalLimitPercentForDepositors: string;
  /** Once the vault-level limit gets used up across depositors, then it goes to individual depositor-level limits */
  withdrawalLimitPercentForVault: string;
};

export type FixedIncomeVaultTransformedConfig = VaultTransformedConfigBase & {
  type: 'fixedIncomeVaultV1';
  originalFields: FixedIncomeVaultProvider_v1.VaultConfigurationFieldsStructOutput;
  effectiveTimestampInS: bigint;
  interestMultiplier: string;
  apy: number;
  /** 0.80 = 80% */
  maximumTotalOwedQuantityAvailableMultiplierToInitiateExit: string;
  /** 1.10 = 110% */
  minimumTotalOwedQuantityAvailableMultiplierToAllowManagerWalletWithdrawal: string;
};

export type ProfitShareVaultTransformedConfig = VaultTransformedConfigBase & {
  type: 'profitShareVaultV1';
  originalFields: ProfitShareVaultProvider_v1.VaultConfigurationFieldsStructOutput;
  /** Share of depositor profits paid to the manager at withdrawal, 0.1 = 10% */
  carryFeeMultiplier: string;
  /** Share of assets under management minted to the manager per fee period, 0.01 = 1% */
  managementFeeMultiplier: string;
};

export type VaultTransformedConfig =
  | FixedIncomeVaultTransformedConfig
  | ProfitShareVaultTransformedConfig;

/**
 * Apply transformations for easier usage on the UI & less computation
 * APY calculation
 *  - https://docs.google.com/spreadsheets/d/1af23exVIHmqagqJ4c1sXkQ_8M2nMIcXgZWiF962Lnkc/edit?gid=932525904#gid=932525904
 *  - based on interest multiplier expecting it to be for 365 days (by default)
 */
export function transformConfigurationFields(
  fieldsRaw: FixedIncomeVaultProvider_v1.VaultConfigurationFieldsStructOutput
): FixedIncomeVaultTransformedConfig {
  // Explicitly pick named properties to ensure a plain object (ethers Result is a tuple + named props)
  const fields: FixedIncomeVaultProvider_v1.VaultConfigurationFieldsStructOutput =
    {
      managerWallet: fieldsRaw.managerWallet,
      effectiveTimestampInS: fieldsRaw.effectiveTimestampInS,
      interestMultiplier: fieldsRaw.interestMultiplier,
      maximumNetDeposits: fieldsRaw.maximumNetDeposits,
      maximumTotalOwedQuantityAvailableMultiplierToInitiateExit:
        fieldsRaw.maximumTotalOwedQuantityAvailableMultiplierToInitiateExit,
      minimumTotalOwedQuantityAvailableMultiplierToAllowManagerWalletWithdrawal:
        fieldsRaw.minimumTotalOwedQuantityAvailableMultiplierToAllowManagerWalletWithdrawal,
      minimumUnappliedDepositOrWithdrawalAgeInSToInitiateExit:
        fieldsRaw.minimumUnappliedDepositOrWithdrawalAgeInSToInitiateExit,
      withdrawalLimitPercentForDepositors:
        fieldsRaw.withdrawalLimitPercentForDepositors,
      withdrawalLimitPercentForVault: fieldsRaw.withdrawalLimitPercentForVault,
    } as FixedIncomeVaultProvider_v1.VaultConfigurationFieldsStructOutput;
  return {
    type: 'fixedIncomeVaultV1',
    originalFields: fields,

    managerWallet: fields.managerWallet,
    effectiveTimestampInS: fields.effectiveTimestampInS,
    /** APY 0.01 = 1% */
    interestMultiplier: pipToDecimal(fields.interestMultiplier),
    // const result = Math.exp(B24 * (B27 / B26)) - 1;
    apy: convertVaultInterestMultiplierToAPY(fields.interestMultiplier),
    maximumNetDeposits: pipToDecimal(fields.maximumNetDeposits),
    maximumTotalOwedQuantityAvailableMultiplierToInitiateExit: pipToDecimal(
      fields.maximumTotalOwedQuantityAvailableMultiplierToInitiateExit
    ),
    minimumTotalOwedQuantityAvailableMultiplierToAllowManagerWalletWithdrawal:
      pipToDecimal(
        fields.minimumTotalOwedQuantityAvailableMultiplierToAllowManagerWalletWithdrawal
      ),
    minimumUnappliedDepositOrWithdrawalAgeInSToInitiateExit:
      fields.minimumUnappliedDepositOrWithdrawalAgeInSToInitiateExit,
    withdrawalLimitPercentForDepositors: pipToDecimal(
      fields.withdrawalLimitPercentForDepositors
    ),
    withdrawalLimitPercentForVault: pipToDecimal(
      fields.withdrawalLimitPercentForVault
    ),
    // Multiply maximumTotalOwedQuantityAvailableMultiplierToInitiateExit by totalOwedQuantity (and divide by 10^8) to get exitTreshold
    // withdrawalLimitPercent is the withdrawal rate
    // withdrawal treshold = minimumTotalOwedQuantityAvailableMultiplierToAllowManagerWalletWithdrawal * totalOwedQuantity / 10^8
  };
}

/**
 * Profit Share provider configuration - fees instead of a fixed yield, no
 * exit / withdrawal thresholds (ownership is tracked by shares) and no
 * effective timestamp (the contract keeps only the current configuration).
 */
export function transformProfitShareConfigurationFields(
  fieldsRaw: ProfitShareVaultProvider_v1.VaultConfigurationFieldsStructOutput
): ProfitShareVaultTransformedConfig {
  const fields: ProfitShareVaultProvider_v1.VaultConfigurationFieldsStructOutput =
    {
      managerWallet: fieldsRaw.managerWallet,
      carryFeeMultiplier: fieldsRaw.carryFeeMultiplier,
      managementFeeMultiplier: fieldsRaw.managementFeeMultiplier,
      maximumNetDeposits: fieldsRaw.maximumNetDeposits,
      minimumUnappliedDepositOrWithdrawalAgeInSToInitiateExit:
        fieldsRaw.minimumUnappliedDepositOrWithdrawalAgeInSToInitiateExit,
      withdrawalLimitPercentForDepositors:
        fieldsRaw.withdrawalLimitPercentForDepositors,
      withdrawalLimitPercentForVault: fieldsRaw.withdrawalLimitPercentForVault,
    } as ProfitShareVaultProvider_v1.VaultConfigurationFieldsStructOutput;
  return {
    type: 'profitShareVaultV1',
    originalFields: fields,

    managerWallet: fields.managerWallet,
    carryFeeMultiplier: pipToDecimal(fields.carryFeeMultiplier),
    managementFeeMultiplier: pipToDecimal(fields.managementFeeMultiplier),
    maximumNetDeposits: pipToDecimal(fields.maximumNetDeposits),
    minimumUnappliedDepositOrWithdrawalAgeInSToInitiateExit:
      fields.minimumUnappliedDepositOrWithdrawalAgeInSToInitiateExit,
    withdrawalLimitPercentForDepositors: pipToDecimal(
      fields.withdrawalLimitPercentForDepositors
    ),
    withdrawalLimitPercentForVault: pipToDecimal(
      fields.withdrawalLimitPercentForVault
    ),
  };
}

/** Form values shared by both vault types */
type VaultConfigFormParamsBase = {
  maximumNetDeposits: string | number;
  withdrawalLimitPercentForDepositors: string | number;
  withdrawalLimitPercentForVault: string | number;
  withdrawalExitAgeInHours: string | number;
};

export type VaultConfigFormParams =
  | (VaultConfigFormParamsBase & {
      type: 'fixedIncomeVaultV1';
      apyPercent: string;
      withdrawalTresholdPercent: string | number;
      exitTresholdPercent: string | number;
    })
  | (VaultConfigFormParamsBase & {
      type: 'profitShareVaultV1';
      /** "10" = 10% of depositor profits */
      carryFeePercent: string | number;
      /** "1" = 1% of assets under management per fee period */
      managementFeePercent: string | number;
    });

const hoursToSeconds = (hours: string | number) =>
  Math.floor(Number(hours) * 3600);

/** Convert form-shaped UI values into the binary payload accepted by the
 *  vault provider contract of the given type. Shared by creation and settings update. */
export function encodeVaultConfigPayload({
  managerWallet,
  effectiveTimestampInS,
  params,
}: {
  managerWallet: string;
  /** Fixed Income only - the Profit Share configuration has no effective timestamp */
  effectiveTimestampInS: number;
  params: VaultConfigFormParams;
}) {
  const sharedFields = {
    managerWallet,
    maximumNetDepositsInPips: decimalToPip(
      trimTo8Decimals(params.maximumNetDeposits)
    ),
    withdrawalLimitPercentForDepositorsInPips: convertPercentToMultiplierInPips(
      params.withdrawalLimitPercentForDepositors
    ),
    withdrawalLimitPercentForVaultInPips: convertPercentToMultiplierInPips(
      params.withdrawalLimitPercentForVault
    ),
  };
  switch (params.type) {
    case 'fixedIncomeVaultV1':
      return encodeFixedIncomeVaultConfigurationFields({
        ...sharedFields,
        effectiveTimestampInS,
        interestMultiplierInPips: convertVaultApyToInterestMultiplierInPips(
          params.apyPercent
        ),
        maximumTotalOwedQuantityAvailableForExitWithdrawalMultiplierNeededToInitiateExitInPips:
          convertPercentToMultiplierInPips(params.exitTresholdPercent),
        minimumTotalOwedQuantityAvailableForExitWithdrawalMultiplierToAllowManagerWalletWithdrawalInPips:
          convertPercentToMultiplierInPips(params.withdrawalTresholdPercent),
        minimumUnappliedWithdrawalAgeInSNeededToInitiateExit: hoursToSeconds(
          params.withdrawalExitAgeInHours
        ),
      });
    case 'profitShareVaultV1':
      return encodeProfitShareVaultConfigurationFields({
        ...sharedFields,
        carryFeeMultiplierInPips: convertPercentToMultiplierInPips(
          params.carryFeePercent
        ),
        managementFeeMultiplierInPips: convertPercentToMultiplierInPips(
          params.managementFeePercent
        ),
        minimumUnappliedDepositOrWithdrawalAgeInSToInitiateExit: hoursToSeconds(
          params.withdrawalExitAgeInHours
        ),
      });
    default:
      throw new UnreachableCaseError(params);
  }
}

type VaultProvider = Pick<ApiVault, 'type' | 'provider'>;

export async function initiateConfigurationChange(
  vault: VaultProvider,
  managerWallet: string,
  updatedParams: VaultConfigFormParams,
  wallet: IWallet
) {
  const signer = await getKatanaWalletSigner(wallet);

  const configUpdateDelayS = await fetchConfigUpdateDelayS(vault);
  const dateNowS = Math.floor(Date.now() / 1000);
  const effectiveTimestampInS = dateNowS + Number(configUpdateDelayS);

  const payload = encodeVaultConfigPayload({
    managerWallet,
    effectiveTimestampInS,
    params: updatedParams,
  });

  console.log('Changed fields: ', updatedParams);
  console.log('Vaults config payload', payload);

  return getVaultProviderContract(vault, signer).initiateManagedAccountUpgrade(
    payload
  );
}

export async function finalizeManagedAccountUpgrade(
  vault: VaultProvider,
  wallet: IWallet
): Promise<ContractTransactionResponse> {
  const signer = await getKatanaWalletSigner(wallet);
  // @ts-expect-error TS6 ethers dual-package types (lib.esm vs lib.commonjs)
  return getVaultProviderContract(
    vault,
    signer
  ).finalizeManagedAccountUpgrade();
}

export async function cancelManagedAccountUpgrade(
  vault: VaultProvider,
  wallet: IWallet
): Promise<ContractTransactionResponse> {
  const signer = await getKatanaWalletSigner(wallet);
  // @ts-expect-error TS6 ethers dual-package types (lib.esm vs lib.commonjs)
  return getVaultProviderContract(vault, signer).cancelManagedAccountUpgrade();
}

function fetchConfigUpdateDelayS(vault: VaultProvider): Promise<bigint> {
  return getVaultProviderContract(
    vault
  ).managedAccountUpgradeBlockTimestampDelayInS();
}

export async function setVaultDepositsEnabled(
  vault: VaultProvider,
  enabled: boolean,
  wallet: IWallet
) {
  const signer = await getKatanaWalletSigner(wallet);
  return getVaultProviderContract(vault, signer).setDepositEnabled(enabled);
}
